export const IN_APP_NOTIFICATION_TYPE = 'IN_APP_ANNOUNCEMENT';

export const IN_APP_DATA_KIND = 'in_app_announcement';

export const CURRENT_SURFACE = 'web';

export type InAppMediaType = 'image' | 'video' | 'audio' | 'document';

export type InAppSeverity = 'info' | 'success' | 'warning' | 'critical';

export const IN_APP_SEVERITIES: InAppSeverity[] = ['info', 'success', 'warning', 'critical'];

export const DEFAULT_IN_APP_SEVERITY: InAppSeverity = 'info';

export type InAppDisplayMode = 'toast' | 'banner' | 'bell' | 'all';

export const IN_APP_DISPLAY_MODES: InAppDisplayMode[] = ['toast', 'banner', 'bell', 'all'];

export const DEFAULT_IN_APP_DISPLAY_MODE: InAppDisplayMode = 'toast';

export function resolveInAppDisplayMode(value: unknown): InAppDisplayMode {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (IN_APP_DISPLAY_MODES as string[]).includes(raw)
    ? (raw as InAppDisplayMode)
    : DEFAULT_IN_APP_DISPLAY_MODE;
}

export type InAppModalFrequency = 'once_per_session' | 'every_reload' | 'once_ever';

export const IN_APP_MODAL_FREQUENCIES: InAppModalFrequency[] = [
  'once_per_session',
  'every_reload',
  'once_ever',
];

export const DEFAULT_IN_APP_MODAL_FREQUENCY: InAppModalFrequency = 'once_per_session';

export const MIN_IN_APP_AUTO_DISMISS_SECONDS = 1;
export const MAX_IN_APP_AUTO_DISMISS_SECONDS = 300;

export function resolveInAppSeverity(value: unknown): InAppSeverity {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (IN_APP_SEVERITIES as string[]).includes(raw)
    ? (raw as InAppSeverity)
    : DEFAULT_IN_APP_SEVERITY;
}

export function resolveInAppModalFrequency(value: unknown): InAppModalFrequency {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return (IN_APP_MODAL_FREQUENCIES as string[]).includes(raw)
    ? (raw as InAppModalFrequency)
    : DEFAULT_IN_APP_MODAL_FREQUENCY;
}

export function resolveInAppAutoDismissSeconds(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(
    Math.max(Math.round(n), MIN_IN_APP_AUTO_DISMISS_SECONDS),
    MAX_IN_APP_AUTO_DISMISS_SECONDS
  );
}

export interface InAppMediaItem {
  type: InAppMediaType;
  url: string;
  caption: string | null;
  fileName: string | null;
  thumbnailUrl: string | null;
}

export interface InAppActionButton {
  id: string;
  label: string;
  url: string;
}

export interface InAppAnnouncement {
  announcementId: string | null;
  bodyFormat: 'markdown' | 'text';
  body: string;
  media: InAppMediaItem[];
  actionUrl: string | null;
  actionLabel: string | null;
  actionButtons: InAppActionButton[];
  deviceTypes: string[];
  sentByEmail: string | null;
  sentAt: string | null;
  suppressToast: boolean;
  expiresAt: string | null;
  severity: InAppSeverity;
  autoDismissSeconds: number | null;
  modalFrequency: InAppModalFrequency;
  displayMode: InAppDisplayMode;
  soundMode: 'default' | 'silent';
  modalRequested: boolean;
}

const MEDIA_TYPES: InAppMediaType[] = ['image', 'video', 'audio', 'document'];

function isSafeMediaUrl(url: unknown): url is string {
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim());
}

export function isSafeInAppActionUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('//')) return false;
  if (trimmed.startsWith('/')) return true;
  return /^https?:\/\//i.test(trimmed);
}

export function isInternalInAppActionUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.trim().startsWith('/');
}

function normalizeMediaItem(raw: unknown): InAppMediaItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;

  const url = typeof item.url === 'string' ? item.url.trim() : '';
  if (!isSafeMediaUrl(url)) return null;

  const requested = typeof item.type === 'string' ? item.type.trim().toLowerCase() : '';
  const type = (MEDIA_TYPES as string[]).includes(requested)
    ? (requested as InAppMediaType)
    : 'document';

  const str = (v: unknown): string | null => {
    const s = typeof v === 'string' ? v.trim() : '';
    return s ? s : null;
  };

  const thumbnailUrl = typeof item.thumbnailUrl === 'string' ? item.thumbnailUrl.trim() : '';

  return {
    type,
    url,
    caption: str(item.caption),
    fileName: str(item.fileName),
    thumbnailUrl: isSafeMediaUrl(thumbnailUrl) ? thumbnailUrl : null,
  };
}

export function parseInAppAnnouncement(data: unknown): InAppAnnouncement | null {
  if (!data || typeof data !== 'object') return null;
  const raw = data as Record<string, unknown>;
  if (raw.kind !== IN_APP_DATA_KIND) return null;

  const body = typeof raw.body === 'string' ? raw.body : '';
  const media = Array.isArray(raw.media)
    ? raw.media.map(normalizeMediaItem).filter((m): m is InAppMediaItem => m !== null)
    : [];

  if (!body.trim() && media.length === 0) return null;

  const actionUrl = isSafeInAppActionUrl(raw.action_url) ? String(raw.action_url).trim() : null;
  const soundMode = raw.sound_mode === 'silent' ? 'silent' : 'default';
  const suppressToast = raw.suppress_toast === true;
  const str = (v: unknown): string | null => {
    const s = typeof v === 'string' ? v.trim() : '';
    return s ? s : null;
  };

  return {
    announcementId: str(raw.announcement_id),
    bodyFormat: raw.body_format === 'text' ? 'text' : 'markdown',
    body,
    media,
    actionUrl,
    actionLabel: str(raw.action_label),
    actionButtons: normalizeActionButtons(raw.action_buttons),
    deviceTypes: Array.isArray(raw.device_types)
      ? raw.device_types.filter((d): d is string => typeof d === 'string')
      : [],
    sentByEmail: str(raw.sent_by_email),
    sentAt: str(raw.sent_at),
    suppressToast,
    soundMode,
    modalRequested: suppressToast && soundMode !== 'silent',
    expiresAt: str(raw.expires_at),
    severity: resolveInAppSeverity(raw.severity),
    autoDismissSeconds: resolveInAppAutoDismissSeconds(raw.auto_dismiss_seconds),
    modalFrequency: resolveInAppModalFrequency(raw.modal_frequency),
    displayMode: resolveInAppDisplayMode(raw.display_mode),
  };
}

function normalizeActionButtons(raw: unknown): InAppActionButton[] {
  if (!Array.isArray(raw)) return [];
  const out: InAppActionButton[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const b = item as Record<string, unknown>;
    const label = typeof b.label === 'string' ? b.label.trim() : '';
    const url = typeof b.url === 'string' ? b.url.trim() : '';
    if (!label || !isSafeInAppActionUrl(url)) continue;
    out.push({
      id: typeof b.id === 'string' && b.id ? b.id : `action_${out.length + 1}`,
      label,
      url,
    });
    if (out.length >= 2) break;
  }
  return out;
}

export function isInAppAnnouncementExpired(
  announcement: Pick<InAppAnnouncement, 'expiresAt'> | null | undefined,
  now: number = Date.now()
): boolean {
  const at = announcement?.expiresAt;
  if (!at) return false;
  const ms = new Date(at).getTime();
  if (Number.isNaN(ms)) return false;
  return ms <= now;
}

export const SEVERITY_TOAST_VARIANT: Record<
  InAppSeverity,
  'info' | 'success' | 'warning' | 'destructive'
> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  critical: 'destructive',
};

export const SEVERITY_PRESENTATION: Record<
  InAppSeverity,
  { label: string; chipClass: string; iconWrapClass: string; accentClass: string }
> = {
  info: {
    label: 'Info',
    chipClass: 'bg-blue-50 text-blue-700 border-blue-200',
    iconWrapClass: 'bg-blue-50 text-blue-600',
    accentClass: 'text-blue-600',
  },
  success: {
    label: 'Success',
    chipClass: 'bg-green-50 text-green-700 border-green-200',
    iconWrapClass: 'bg-green-50 text-green-600',
    accentClass: 'text-green-600',
  },
  warning: {
    label: 'Warning',
    chipClass: 'bg-amber-50 text-amber-700 border-amber-200',
    iconWrapClass: 'bg-amber-50 text-amber-600',
    accentClass: 'text-amber-600',
  },
  critical: {
    label: 'Critical',
    chipClass: 'bg-red-50 text-red-700 border-red-200',
    iconWrapClass: 'bg-red-50 text-red-600',
    accentClass: 'text-red-600',
  },
};

export function inAppToastDurationMs(
  announcement: Pick<InAppAnnouncement, 'autoDismissSeconds'> | null | undefined
): number | undefined {
  if (!announcement) return undefined;
  const seconds = announcement.autoDismissSeconds;
  if (seconds === null || seconds === undefined) return Infinity;
  if (!Number.isFinite(seconds) || seconds <= 0) return Infinity;
  return seconds * 1000;
}

export interface InAppPlacement {
  pill: boolean;
  banner: boolean;
}

const NO_PLACEMENT: InAppPlacement = { pill: false, banner: false };

export function resolveInAppPlacement(
  announcement: InAppAnnouncement | null | undefined,
  now: number = Date.now()
): InAppPlacement {
  if (!announcement) return NO_PLACEMENT;
  if (isInAppAnnouncementExpired(announcement, now)) return NO_PLACEMENT;
  if (!isForCurrentSurface(announcement)) return NO_PLACEMENT;
  if (announcement.soundMode === 'silent') return NO_PLACEMENT;

  switch (announcement.displayMode) {
    case 'bell':
      return NO_PLACEMENT;
    case 'banner':
      return { pill: false, banner: true };
    case 'all':
      return { pill: true, banner: true };
    case 'toast':
    default:
      return { pill: true, banner: false };
  }
}

export function inAppAnnouncementOf(notification: unknown): InAppAnnouncement | null {
  if (!notification || typeof notification !== 'object') return null;
  const n = notification as Record<string, unknown>;
  if (String(n.type || '').toUpperCase() !== IN_APP_NOTIFICATION_TYPE) return null;
  return parseInAppAnnouncement(n.data);
}

export function isForCurrentSurface(
  announcement: Pick<InAppAnnouncement, 'deviceTypes'> | null | undefined,
  surface: string = CURRENT_SURFACE
): boolean {
  if (!announcement) return false;
  const types = announcement.deviceTypes;
  if (!Array.isArray(types) || types.length === 0) return true;
  return types.includes(surface);
}

export function shouldSuppressToast(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  return (data as Record<string, unknown>).suppress_toast === true;
}

export function mediaTypeLabel(type: InAppMediaType): string {
  switch (type) {
    case 'image':
      return 'Image';
    case 'video':
      return 'Video';
    case 'audio':
      return 'Audio';
    default:
      return 'File';
  }
}

export function guessMediaType(url: string): InAppMediaType {
  const clean = String(url).split(/[?#]/)[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|avif|bmp|svg)$/.test(clean)) return 'image';
  if (/\.(mp4|webm|mov|m4v|3gp)$/.test(clean)) return 'video';
  if (/\.(mp3|wav|ogg|m4a|aac|amr)$/.test(clean)) return 'audio';
  return 'document';
}

export function inAppDeliverySummary(metadata: unknown): string {
  if (!metadata || typeof metadata !== 'object') return '';
  const live = Number((metadata as Record<string, unknown>).liveSocketDeliveries);
  if (!Number.isFinite(live) || live <= 0) return '';
  return ` · ${live} user${live === 1 ? '' : 's'} notified live`;
}
